/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowUpCircle, CheckCircle2, Circle, Clock } from "lucide-react";
import apiService from "@/services/apiService";
import useErrorStore from "@/store/confirmationStore";
import { showValidationToast } from "@/lib/toastHelper";
import { validateRequired } from "@/lib/validationHelper";
import { getUserInfo } from "@/services/userService";
import PdfPreview from "./PdfPreview";
import { StageUpdateDialog } from "./StageUpdatePopup";

export interface FIRDetail {
  firNumber: string;
  sections?: string;
  reducedSections?: string;
  addedSections?: string;
  chargeSheetNumber?: string;
  policeStation?: string;
  result?: string;
  details?: string;
}

export interface AdditionalEvidence {
  type: string;
  description: string;
  chargeSheetNo?:any;
  firNumber?:any;
  result?:any;
  sections?:any;
  details?:any;
}

export interface AccusedDetails {
  name: string;
  fatherName: string;
  age: string;
  address: string;
  reputation: string;
  habitualOffender: string;
  threatLevel: string;
}

export interface CourtDetails {
  courtName: string;
  caseStatus: string;
  finalOrderDate: string;
  finalOrderText: string;
}

export interface GoondaCasestages {
  id?: string;
  stage: string;
  actionDate: string;
  remarks?: string;
  performedBy?: string;
  designation?: string;
}

export interface GoondaActCaseForm {
  id?: string;
  caseNumber: string;
  year: string;
  district: string;
  policeStation: string;
  actSection: string;
  reportDate: string;
  subject: string;
  reportSummary: string;
  accusedDetails: AccusedDetails;
  goondaType: string[];
  firDetails: FIRDetail[];
  evidences: AdditionalEvidence[];
  courtDetails: CourtDetails;
  stages?: GoondaCasestages[];
  createdBy?: any;
  createdAt?:any;
  accusedName?:any;
  fatherName?:any;
  age?:any;
  address?:any;
  reputation?:any;
  habitual?:any;
  threatLevel?:any;
  firNumber?:any;
  sections?:any;
  additionalActs?:any;
  courtName?:any;
  status?:any;
  finalOrderDate?:any;
  finalOrderText?:any;
  attachments?:any;
}

interface CriminalFormPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data?: Partial<GoondaActCaseForm>;
  mode?:string;
}

const CASE_STAGES = [
  { key: "SHO_REPORT", label: "SHO Report", description: "SHO submitted the report" },
  { key: "CO_SIGN", label: "CO Approval", description: "Circle Officer approval" },
  { key: "SDM_SIGN", label: "SDM Approval", description: "Sub Divisional Magistrate approval" },
  { key: "SP_PERMISSION", label: "SP Permission", description: "Superintendent of Police permission" },
  { key: "GD_ENTRY", label: "GD Entry", description: "GD entry in police station" },
  { key: "DM_NOTICE_ISSUED", label: "DM Notice", description: "District Magistrate Court issued notice" },
  { key: "UNDER_HEARING", label: "Under Hearing", description: "Case under hearing" },
  { key: "FINAL_ORDER", label: "Final Order", description: "Final judgment / order issued" },
];

const ALLEGATION_OPTIONS = [
  "Rioting",
  "Human Trafficking",
  "Alcohol Smuggling",
  "Arms Smuggling",
  "Illegal Gambling",
  "Dangerous to the community",
  "Eve-teasing/Sexual Harassment/Cyber Sexual Harassment",
  "Tout",
  "House-grabbing",
  "Money Laundering",
  "Forest Act Crimes",
  "UAPA Crimes",
  "Cow Smuggling",
];

export function CriminalFormPopup({ open, onOpenChange, data, mode = "edit" }: CriminalFormPopupProps) {
  const showError = useErrorStore((s) => s.showError);
  const fileBase64 = sessionStorage.getItem('uploaded_pdf_base64')
  const [formData, setFormData] = useState<Partial<GoondaActCaseForm>>({
    goondaType: [],
    firDetails: [],
    evidences: [],
    stages: [],
    attachments: [],
    accusedDetails: {
      name: "",
      fatherName: "",
      age: "",
      address: "",
      reputation: "",
      habitualOffender: "",
      threatLevel: "",
    },
    courtDetails: {
      courtName: "",
      caseStatus: "",
      finalOrderDate: "",
      finalOrderText: "",
    },
  });
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [showErrors, setShowErrors] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(mode == "view");
  const [selectedAllegation, setSelectedAllegation] = useState("");

  useEffect(() => {
    if (data) {
      setFormData({
        ...data,
        accusedDetails: data.accusedDetails || {
          name: data.accusedName,
          fatherName: data.fatherName,
          age: data.age,
          address: data.address,
          reputation: data.reputation,
          habitualOffender: data.habitual,
          threatLevel: data.threatLevel,
        },
        courtDetails: data.courtDetails || {
          courtName: data.courtName,
          caseStatus: data.status,
          finalOrderDate: data.finalOrderDate,
          finalOrderText: data.finalOrderText,
        },
        goondaType: data.goondaType || [],
        firDetails: data.firDetails || [{
          firNumber:data.firNumber,
          sections:data.sections,
          reducedSections:"",
          addedSections:data.additionalActs,
          chargeSheetNumber:"",
          policeStation:data.policeStation,
          result:"",
          details:"",
        }],
        evidences: data.evidences || [],
        stages: data.stages || [],
      });
    }
  }, [data]);

  const getStageStatus = (stageKey: string): "completed" | "pending" | "current" => {
    const history = formData.stages || [];
    const stageIndex = CASE_STAGES.findIndex((s) => s.key === stageKey);
    const completedStages = history.filter((h) => h.actionDate).map((h) => h.stage);
    
    if (completedStages.includes(stageKey)) return "completed";
    
    // Find first pending stage
    const firstPendingIndex = CASE_STAGES.findIndex((s) => !completedStages.includes(s.key));
    if (stageIndex === firstPendingIndex) return "current";
    
    return "pending";
  };

  const getStageInfo = (stageKey: string) => {
    return formData.stages?.find((h) => h.stage === stageKey);
  };

  const validateForm = (): boolean => {
    const errors: any = {};
    let hasErrors = false;

    const requiredFields = [
      "caseNumber",
      "year",
      "district",
      "policeStation",
      "actSection",
      "subject",
      "reportSummary",
    ];

    requiredFields.forEach((field) => {
      const error = validateRequired(
        formData[field as keyof GoondaActCaseForm],
        field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
      );
      if (error) {
        errors[field] = error;
        hasErrors = true;
      }
    });

    // Validate accused details
    const accusedFields = ["name", "fatherName", "age", "address"];
    errors.accusedDetails = {};
    accusedFields.forEach((field) => {
      if (!formData.accusedDetails?.[field as keyof AccusedDetails]) {
        errors.accusedDetails[field] = `${field.replace(/([A-Z])/g, " $1")} is required`;
        hasErrors = true;
      }
    });

    // Validate FIR details
    if (formData.firDetails && formData.firDetails.length > 0) {
      errors.firDetails = [];
      formData.firDetails.forEach((fir, idx) => {
        const e: any = {};
        if (!fir.firNumber) e.firNumber = "FIR Number is required";
        if (!fir.sections) e.sections = "Sections is required";
        if (!fir.policeStation) e.policeStation = "Police Station is required";
        if (!fir.result) e.result = "Result is required";
        if (Object.keys(e).length) {
          hasErrors = true;
          errors.firDetails[idx] = e;
        }
      });
    }

    setValidationErrors(errors);
    return !hasErrors;
  };

  const handleInputChange = (field: keyof GoondaActCaseForm, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccusedChange = (key: keyof AccusedDetails, value: string) => {
    setFormData((prev) => ({
      ...prev,
      accusedDetails: { ...prev.accusedDetails!, [key]: value },
    }));
  };

  const handleCourtChange = (key: keyof CourtDetails, value: string) => {
    setFormData((prev) => ({
      ...prev,
      courtDetails: { ...prev.courtDetails!, [key]: value },
    }));
  };

  const handleFIRChange = (index: number, key: keyof FIRDetail, value: string) => {
    setFormData((prev) => {
      const updated = [...(prev.firDetails || [])];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, firDetails: updated };
    });
  };

  const handleEvidenceChange = (index: number, key: keyof AdditionalEvidence, value: string) => {
    setFormData((prev) => {
      const updated = [...(prev.evidences || [])];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, evidences: updated };
    });
  };

  const addFIR = () => {
    setFormData((prev) => ({
      ...prev,
      firDetails: [
        ...(prev.firDetails || []),
        {
          firNumber: "",
          sections: "",
          reducedSections: "",
          addedSections: "",
          chargeSheetNumber: "",
          policeStation: "",
          result: "",
          details: "",
        },
      ],
    }));
  };

  const addEvidence = () => {
    setFormData((prev) => ({
      ...prev,
      additionalEvidence: [...(prev.evidences || []), { type: "", description: "" }],
    }));
  };

  const addAllegation = (value: string) => {
    if (value && !formData.goondaType?.includes(value)) {
      setFormData((prev) => ({
        ...prev,
        goondaType: [...(prev.goondaType || []), value],
      }));
      setSelectedAllegation("");
    }
  };

  const removeAllegation = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      goondaType: prev.goondaType?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSubmit = async () => {
    setShowErrors(true);
    if (!validateForm()) {
      showValidationToast(validationErrors);
      return;
    }

    try {
      const userInfo: any = getUserInfo();
      const postParams = {
        caseNumber: formData.caseNumber,
        year: Number(formData.year),
        district: formData.district,
        policeStation: formData.policeStation,
        actSection: formData.actSection,
        reportDate: formData.reportDate && !isNaN(new Date(formData.reportDate).getTime()) ? new Date(formData.reportDate) : new Date(),
        subject: formData.subject,
        reportSummary: formData.reportSummary,
        goondaType: formData.goondaType?.join(","),

        // Accused Info
        accusedName: formData.accusedDetails?.name,
        fatherName: formData.accusedDetails?.fatherName,
        address: formData.accusedDetails?.address,
        age: Number(String(formData.accusedDetails?.age || "").replace(/\D/g, "")) || null,
        reputation: formData.accusedDetails?.reputation,
        habitual:
          formData.accusedDetails?.habitualOffender === "हां" ||
          formData.accusedDetails?.habitualOffender === "हाँ",
        threatLevel: formData.accusedDetails?.threatLevel,

        // Related Acts / FIR (Not stored in main table — handled in evidences)
        firNumber: formData.firDetails?.[0]?.firNumber,
        sections: formData.firDetails?.[0]?.sections,
        additionalActs: formData.firDetails?.[0]?.addedSections,

        // Workflow Tracking
        currentStage: "SHO_REPORT",
        status: "ACTIVE",
        remarks: null,

        // Court Info
        courtName: formData.courtDetails?.courtName,
        caseStatus: formData.courtDetails?.caseStatus,
        finalOrderDate: formData.courtDetails?.finalOrderDate
          ? new Date(formData.courtDetails?.finalOrderDate)
          : new Date(),
        finalOrderText: formData.courtDetails?.finalOrderText,

        // Relations
        evidences: {
          create: formData.firDetails?.map((f) => ({
            firNumber: f.firNumber,
            sections: f.sections,
            chargeSheetNo: f.chargeSheetNumber,
            result: f.result,
            details: f.details,
          })),
        },

        attachments: {
          create: fileBase64
            ? [
                {
                  fileType: "REPORT",
                  filePath: "N/A",
                  fileBase64: fileBase64,
                  uploadedBy: { connect: { id: userInfo.id } },
                },
              ]
            : [],
        },

        stages: {
          create: [
            {
              stage: "SHO_REPORT",
              remarks: "Initial report submission",
              performedBy: userInfo.name, // or any user identifier you want
              designation: "SHO",
            },
          ],
        },

        // Created By Relation
        createdBy: formData.createdBy,
      };
      if (userInfo) {
        postParams.createdBy = { connect: { id: userInfo.id } };
      }

      const response = await apiService.post("goonda-act-case", JSON.stringify(postParams));
      if (response) {
        toast.success("Goonda Act Case saved successfully!", {
          description: "Case record has been created.",
        });
        onOpenChange(false);
      } else {
        showError("Failed to save case. Please check the form and try again.");
      }
    } catch (error: any) {
      showError(error);
    }
  };

  const getFieldError = (field: string) => validationErrors[field] || "";
  const getNestedError = (parent: string, field: string) => 
    validationErrors[parent]?.[field] || "";

  const [showStageUpdate, setShowStageUpdate] = useState(false);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[90vh] bg-white flex flex-col w-[95%]" style={{ maxWidth: "1200px" }}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-black">Goonda Act Case Report</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-4">
          {/* Case Stage Timeline */}
          {formData.stages && formData.stages.length > 0 && (
            <Card className="border border-gray-200 mb-4">
              <CardHeader className="grid grid-cols-2">
                <CardTitle className="text-lg text-black">Case Progress Timeline</CardTitle>
                <Button
                  onClick={() => setShowStageUpdate(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <ArrowUpCircle className="w-4 h-4 mr-2" />
                  Update Case Progress
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {CASE_STAGES.map((stage, index) => {
                    const status = getStageStatus(stage.key);
                    const stageInfo = getStageInfo(stage.key);
                    
                    return (
                      <div key={stage.key} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          {status === "completed" ? (
                            <CheckCircle2 className="w-8 h-8 text-green-600" />
                          ) : status === "current" ? (
                            <Clock className="w-8 h-8 text-blue-600" />
                          ) : (
                            <Circle className="w-8 h-8 text-gray-300" />
                          )}
                          {index < CASE_STAGES.length - 1 && (
                            <div className={`w-0.5 h-12 mt-2 ${status === "completed" ? "bg-green-600" : "bg-gray-300"}`} />
                          )}
                        </div>
                        
                        <div className="flex-1 pb-8">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className={`font-semibold ${status === "completed" ? "text-green-700" : status === "current" ? "text-blue-700" : "text-gray-500"}`}>
                                {stage.label}
                              </h4>
                              <p className="text-sm text-gray-600">{stage.description}</p>
                            </div>
                            <div className="text-right">
                              {status === "completed" && stageInfo && (
                                <>
                                  <p className="text-xs text-gray-500">
                                    {new Date(stageInfo.actionDate).toLocaleDateString('en-IN')}
                                  </p>
                                  {stageInfo.performedBy && (
                                    <p className="text-xs text-gray-600">{stageInfo.performedBy}</p>
                                  )}
                                  {stageInfo.designation && (
                                    <p className="text-xs text-gray-500">{stageInfo.designation}</p>
                                  )}
                                </>
                              )}
                              {status === "current" && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">In Progress</span>
                              )}
                              {status === "pending" && (
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Pending</span>
                              )}
                            </div>
                          </div>
                          {status === "completed" && stageInfo?.remarks && (
                            <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded">{stageInfo.remarks}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Basic Case Details */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Case Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Case Number <span className="text-red-500">*</span></Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.caseNumber || ""}
                  onChange={(e) => handleInputChange("caseNumber", e.target.value)}
                  className={showErrors && getFieldError("caseNumber") ? "border-red-500 focus:ring-red-500" : ""}
                />
                {showErrors && getFieldError("caseNumber") && <div className="text-red-600 text-sm mt-1">{getFieldError("caseNumber")}</div>}
              </div>
              <div>
                <Label>Year <span className="text-red-500">*</span></Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.year || ""}
                  onChange={(e) => handleInputChange("year", e.target.value)}
                  className={showErrors && getFieldError("year") ? "border-red-500 focus:ring-red-500" : ""}
                />
                {showErrors && getFieldError("year") && <div className="text-red-600 text-sm mt-1">{getFieldError("year")}</div>}
              </div>
              <div>
                <Label>District <span className="text-red-500">*</span></Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.district || ""}
                  onChange={(e) => handleInputChange("district", e.target.value)}
                  className={showErrors && getFieldError("district") ? "border-red-500 focus:ring-red-500" : ""}
                />
                {showErrors && getFieldError("district") && <div className="text-red-600 text-sm mt-1">{getFieldError("district")}</div>}
              </div>
              <div>
                <Label>Police Station <span className="text-red-500">*</span></Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.policeStation || ""}
                  onChange={(e) => handleInputChange("policeStation", e.target.value)}
                  className={showErrors && getFieldError("policeStation") ? "border-red-500 focus:ring-red-500" : ""}
                />
                {showErrors && getFieldError("policeStation") && <div className="text-red-600 text-sm mt-1">{getFieldError("policeStation")}</div>}
              </div>
              <div>
                <Label>Act Section <span className="text-red-500">*</span></Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.actSection || ""}
                  onChange={(e) => handleInputChange("actSection", e.target.value)}
                  className={showErrors && getFieldError("actSection") ? "border-red-500 focus:ring-red-500" : ""}
                />
                {showErrors && getFieldError("actSection") && <div className="text-red-600 text-sm mt-1">{getFieldError("actSection")}</div>}
              </div>
              <div>
                <Label>PS Panjikaran Date</Label>
                <Input
                  type="date"
                  disabled={isPreviewMode}
                  value={formData.reportDate || ""}
                  onChange={(e) => handleInputChange("reportDate", e.target.value)}
                />
              </div>
              <div className="md:col-span-3">
                <Label>Subject <span className="text-red-500">*</span></Label>
                <Textarea
                  disabled={isPreviewMode}
                  rows={2}
                  value={formData.subject || ""}
                  onChange={(e) => handleInputChange("subject", e.target.value)}
                  className={showErrors && getFieldError("subject") ? "border-red-500 focus:ring-red-500" : ""}
                />
                {showErrors && getFieldError("subject") && <div className="text-red-600 text-sm mt-1">{getFieldError("subject")}</div>}
              </div>
              <div className="md:col-span-3">
                <Label>Report Summary <span className="text-red-500">*</span></Label>
                <Textarea
                  disabled={isPreviewMode}
                  rows={4}
                  value={formData.reportSummary || ""}
                  onChange={(e) => handleInputChange("reportSummary", e.target.value)}
                  className={showErrors && getFieldError("reportSummary") ? "border-red-500 focus:ring-red-500" : ""}
                />
                {showErrors && getFieldError("reportSummary") && <div className="text-red-600 text-sm mt-1">{getFieldError("reportSummary")}</div>}
              </div>
            </CardContent>
          </Card>

          {/* Accused Details */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Accused Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Name <span className="text-red-500">*</span></Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.accusedDetails?.name || ""}
                  onChange={(e) => handleAccusedChange("name", e.target.value)}
                  className={showErrors && getNestedError("accusedDetails", "name") ? "border-red-500 focus:ring-red-500" : ""}
                />
                {showErrors && getNestedError("accusedDetails", "name") && <div className="text-red-600 text-sm mt-1">{getNestedError("accusedDetails", "name")}</div>}
              </div>
              <div>
                <Label>Father Name <span className="text-red-500">*</span></Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.accusedDetails?.fatherName || ""}
                  onChange={(e) => handleAccusedChange("fatherName", e.target.value)}
                  className={showErrors && getNestedError("accusedDetails", "fatherName") ? "border-red-500 focus:ring-red-500" : ""}
                />
                {showErrors && getNestedError("accusedDetails", "fatherName") && <div className="text-red-600 text-sm mt-1">{getNestedError("accusedDetails", "fatherName")}</div>}
              </div>
              <div>
                <Label>Age <span className="text-red-500">*</span></Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.accusedDetails?.age || ""}
                  onChange={(e) => handleAccusedChange("age", e.target.value)}
                  className={showErrors && getNestedError("accusedDetails", "age") ? "border-red-500 focus:ring-red-500" : ""}
                />
                {showErrors && getNestedError("accusedDetails", "age") && <div className="text-red-600 text-sm mt-1">{getNestedError("accusedDetails", "age")}</div>}
              </div>
              <div>
                <Label>Reputation</Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.accusedDetails?.reputation || ""}
                  onChange={(e) => handleAccusedChange("reputation", e.target.value)}
                />
              </div>
              <div>
                <Label>Habitual Offender</Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.accusedDetails?.habitualOffender || ""}
                  onChange={(e) => handleAccusedChange("habitualOffender", e.target.value)}
                />
              </div>
              <div>
                <Label>Threat Level</Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.accusedDetails?.threatLevel || ""}
                  onChange={(e) => handleAccusedChange("threatLevel", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Address <span className="text-red-500">*</span></Label>
                <Textarea
                  disabled={isPreviewMode}
                  rows={2}
                  value={formData.accusedDetails?.address || ""}
                  onChange={(e) => handleAccusedChange("address", e.target.value)}
                  className={showErrors && getNestedError("accusedDetails", "address") ? "border-red-500 focus:ring-red-500" : ""}
                />
                {showErrors && getNestedError("accusedDetails", "address") && <div className="text-red-600 text-sm mt-1">{getNestedError("accusedDetails", "address")}</div>}
              </div>
            </CardContent>
          </Card>

          {/* General Nature of Allegations */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Goonda Type</CardTitle>
            </CardHeader>
            <CardContent>
              {!isPreviewMode && (
                <div className="flex gap-2 mb-3">
                  <Select
                    value={selectedAllegation}
                    onValueChange={(value) => {
                      setSelectedAllegation(value);
                      addAllegation(value);
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select an goonda type to add" />
                    </SelectTrigger>
                    <SelectContent>
                      {ALLEGATION_OPTIONS.filter(
                        (option) => !formData.goondaType?.includes(option)
                      ).map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {(formData.goondaType || []).map((allegation, i) => (
                  <div key={i} className="bg-gray-100 px-3 py-1 rounded-full flex items-center gap-2">
                    <span>{allegation}</span>
                    {!isPreviewMode && (
                      <button onClick={() => removeAllegation(i)} className="text-red-600 hover:text-red-800">×</button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* FIR Details */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader className="flex justify-between items-center">
              <CardTitle className="text-lg text-black">FIR Details</CardTitle>
              {!isPreviewMode && <Button variant={"outline"} size="sm" onClick={addFIR}>Add FIR</Button>}
            </CardHeader>
            <CardContent className="space-y-4">
              {(formData.firDetails || []).map((fir, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3 border p-3 rounded-md">
                  <div>
                    <Label>FIR Number <span className="text-red-500">*</span></Label>
                    <Input
                      disabled={isPreviewMode}
                      value={fir.firNumber}
                      onChange={(e) => handleFIRChange(i, "firNumber", e.target.value)}
                      className={showErrors && validationErrors.firDetails?.[i]?.firNumber ? "border-red-500 focus:ring-red-500" : ""}
                    />
                    {showErrors && validationErrors.firDetails?.[i]?.firNumber && (
                      <div className="text-red-600 text-sm mt-1">{validationErrors.firDetails[i].firNumber}</div>
                    )}
                  </div>
                  <div>
                    <Label>Sections <span className="text-red-500">*</span></Label>
                    <Input
                      disabled={isPreviewMode}
                      value={fir.sections}
                      onChange={(e) => handleFIRChange(i, "sections", e.target.value)}
                      className={showErrors && validationErrors.firDetails?.[i]?.sections ? "border-red-500 focus:ring-red-500" : ""}
                    />
                    {showErrors && validationErrors.firDetails?.[i]?.sections && (
                      <div className="text-red-600 text-sm mt-1">{validationErrors.firDetails[i].sections}</div>
                    )}
                  </div>
                  <div>
                    <Label>Reduced Sections</Label>
                    <Input
                      disabled={isPreviewMode}
                      value={fir.reducedSections}
                      onChange={(e) => handleFIRChange(i, "reducedSections", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Added Sections</Label>
                    <Input
                      disabled={isPreviewMode}
                      value={fir.addedSections}
                      onChange={(e) => handleFIRChange(i, "addedSections", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Charge Sheet Number</Label>
                    <Input
                      disabled={isPreviewMode}
                      value={fir.chargeSheetNumber}
                      onChange={(e) => handleFIRChange(i, "chargeSheetNumber", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Police Station <span className="text-red-500">*</span></Label>
                    <Input
                      disabled={isPreviewMode}
                      value={fir.policeStation}
                      onChange={(e) => handleFIRChange(i, "policeStation", e.target.value)}
                      className={showErrors && validationErrors.firDetails?.[i]?.policeStation ? "border-red-500 focus:ring-red-500" : ""}
                    />
                    {showErrors && validationErrors.firDetails?.[i]?.policeStation && (
                      <div className="text-red-600 text-sm mt-1">{validationErrors.firDetails[i].policeStation}</div>
                    )}
                  </div>
                  <div>
                    <Label>Result <span className="text-red-500">*</span></Label>
                    <Input
                      disabled={isPreviewMode}
                      value={fir.result}
                      onChange={(e) => handleFIRChange(i, "result", e.target.value)}
                      className={showErrors && validationErrors.firDetails?.[i]?.result ? "border-red-500 focus:ring-red-500" : ""}
                    />
                    {showErrors && validationErrors.firDetails?.[i]?.result && (
                      <div className="text-red-600 text-sm mt-1">{validationErrors.firDetails[i].result}</div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <Label>Details</Label>
                    <Textarea
                      disabled={isPreviewMode}
                      rows={3}
                      value={fir.details}
                      onChange={(e) => handleFIRChange(i, "details", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Additional Evidence */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader className="flex justify-between items-center">
              <CardTitle className="text-lg text-black">Additional Evidence</CardTitle>
              {!isPreviewMode && <Button variant={"outline"} size="sm" onClick={addEvidence}>Add Evidence</Button>}
            </CardHeader>
            <CardContent className="space-y-4">
              {(formData.evidences || []).map((ev, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3 border p-3 rounded-md">
                  <div>
                    <Label>Charge Sheet No</Label>
                    <Input
                      disabled={isPreviewMode}
                      value={ev.chargeSheetNo}
                      onChange={(e) => handleEvidenceChange(i, "chargeSheetNo", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>FIR Number</Label>
                    <Input
                      disabled={isPreviewMode}
                      value={ev.firNumber}
                      onChange={(e) => handleEvidenceChange(i, "firNumber", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Result</Label>
                    <Input
                      disabled={isPreviewMode}
                      value={ev.result}
                      onChange={(e) => handleEvidenceChange(i, "result", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Sections</Label>
                    <Input
                      disabled={isPreviewMode}
                      value={ev.sections}
                      onChange={(e) => handleEvidenceChange(i, "sections", e.target.value)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Details</Label>
                    <Textarea
                      disabled={isPreviewMode}
                      rows={3}
                      value={ev.details}
                      onChange={(e) => handleEvidenceChange(i, "details", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Court Details */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Court Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Court Name</Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.courtDetails?.courtName || ""}
                  onChange={(e) => handleCourtChange("courtName", e.target.value)}
                />
              </div>
              <div>
                <Label>Case Status</Label>
                <Input
                  disabled={isPreviewMode}
                  value={formData.courtDetails?.caseStatus || ""}
                  onChange={(e) => handleCourtChange("caseStatus", e.target.value)}
                />
              </div>
              <div>
                <Label>Final Order Date</Label>
                <Input
                  type="date"
                  disabled={isPreviewMode}
                  value={formData.courtDetails?.finalOrderDate || ""}
                  onChange={(e) => handleCourtChange("finalOrderDate", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Final Order Text</Label>
                <Textarea
                  disabled={isPreviewMode}
                  rows={3}
                  value={formData.courtDetails?.finalOrderText || ""}
                  onChange={(e) => handleCourtChange("finalOrderText", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* PDF Preview */}
          {formData.attachments && formData.attachments.filter((attachment:any) => attachment.fileBase64).map((attachment:any) => (
            <Card className="border border-gray-200 mb-4" key={attachment.id}>
              <CardHeader>
                <CardTitle className="text-lg text-black">PDF Preview: {attachment.fileType}</CardTitle>
              </CardHeader>
              <PdfPreview base64Pdf={attachment.fileBase64} />
            </Card>
          ))}
        </div>

        {/* Footer */}
        <div className={mode == "view" ? "hidden" :"flex justify-end space-x-2 pt-4 border-t bg-white"}>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {isPreviewMode ? (
            <>
              <Button variant="outline" onClick={() => setIsPreviewMode(false)}>Edit Again</Button>
              <Button onClick={handleSubmit} className="bg-[#0d2846] text-white">Confirm & Submit</Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setShowErrors(true);
                if (validateForm()) {
                  setIsPreviewMode(true);
                  document.querySelector(".flex-1.overflow-y-auto.pr-4")?.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                  showValidationToast(validationErrors);
                }
              }}
              className="bg-[#0d2846] text-white"
            >
              Preview Before Submit
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
    {/* Stage Update Dialog */}
    {data?.id && (
        <StageUpdateDialog
          open={showStageUpdate}
          onOpenChange={setShowStageUpdate}
          caseId={data.id}
          currentStages={data.stages?.map((s: any) => s.stage) || []}
          onSuccess={() => {
            // Refresh the case data
            // You might want to call a refetch function here
          }}
        />
    )}
    </>
  );
}